/**
 * Shrink a camera photo before it is sent.
 *
 * <p>Extracted from {@code PhotoQueue}, which solved this first for inspection photos — *"at the
 * 1 KB/s some accommodations manage, that never finishes"* — and now delegates here. Same user
 * base, same arithmetic: a student photographing an ID on mobile data at 23 KB/s needs a 4 MB
 * photo to become ~250 KB, or the upload cannot finish inside Cloudflare's 100-second limit.</p>
 *
 * <p>See {@code APPLY_UPLOAD_DESIGN.md} for the measurements behind the constants.</p>
 */

/** Longest edge after compression. Big enough to read an ID number, small enough to send. */
export const MAX_EDGE = 1600;
export const JPEG_QUALITY = 0.7;

/** Below this, re-encoding costs more than it saves. */
const ALREADY_SMALL = 600_000;

/**
 * Compress an image; return anything else untouched.
 *
 * <p><b>Never throws and never returns nothing.</b> Every failure path falls back to the original
 * file, because a large photo is better than no photo — the student has already taken it, and an
 * application that cannot be submitted is worse than one that is slow.</p>
 *
 * @param file the file as chosen by the student
 * @returns the blob to upload — the original instance when nothing was gained
 */
export async function compressImage(file: File): Promise<Blob> {
    if (!isCompressibleImage(file)) return file;

    try {
        const bitmap = await decode(file);
        const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
        if (scale === 1 && file.size < ALREADY_SMALL) {
            bitmap.close?.();
            return file;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            bitmap.close?.();
            return file;
        }
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close?.();

        const blob = await new Promise<Blob | null>(
            resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
        return blob && blob.size < file.size ? blob : file;
    } catch (e) {
        console.warn('[upload] Could not compress, keeping the original', e);
        return file;
    }
}

/**
 * PDFs and office documents pass straight through. Rasterising a PDF in the browser would mean
 * shipping a megabyte of JavaScript to the very connections this exists to help, and would throw
 * away the text layer.
 */
function isCompressibleImage(file: File): boolean {
    return file.type.startsWith('image/') && file.type !== 'image/svg+xml';
}

/**
 * Decode honouring the EXIF orientation tag.
 *
 * <p>Drawing to a canvas discards that tag, so without this a phone-held-portrait photo of an ID
 * arrives rotated — on the acceptance letter and in front of staff. Inspection photos of a scuffed
 * wall did not care; an identity document does.</p>
 *
 * <p>The option is ignored rather than rejected by some older engines, so the fallback is a plain
 * decode: a sideways photo still beats a failed upload.</p>
 */
async function decode(file: File): Promise<ImageBitmap> {
    try {
        return await createImageBitmap(file, {imageOrientation: 'from-image'});
    } catch {
        return await createImageBitmap(file);
    }
}

/** "4.2 MB", "248 KB" — for telling a student which file is the problem. */
export function formatFileSize(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
